<template>
  <div ref="dropdownRef" class="dropdown">
    <ul class="dropdown-content">
      <li
        v-for="(subItem, index) in item.items"
        :key="`${index}-${subItem.label}`"
        class="list-item"
      >
        <slot :sub-item="subItem" />
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { useWindowSize } from '@vueuse/core'
import { debounce } from 'lodash-es'

import { DESKTOP_LINKS_ID } from '@/constants/navigation'
import type { PublicMenuItem } from '@/service/__generated-api'

defineProps<{ item: PublicMenuItem }>()

const dropdownRef = ref<HTMLDivElement | null>(null)
const maxWidth = ref('unset')
const translateX = ref('0')

const onWindowResize = debounce((width: number): void => {
  const desktopLinksRect = document
    .getElementById(DESKTOP_LINKS_ID)
    ?.getBoundingClientRect()

  if (!dropdownRef.value || width < 1024 || !desktopLinksRect) return
  const dropdownRect = dropdownRef.value.getBoundingClientRect()

  maxWidth.value = desktopLinksRect.width + 'px'

  const dropdownWidth =
    dropdownRect.width > desktopLinksRect.width
      ? desktopLinksRect.width
      : dropdownRect.width
  const dropdownRight = dropdownRect.x + dropdownWidth

  const stickingOutValue = Math.max(dropdownRight - desktopLinksRect.right, 0)

  translateX.value = `-${stickingOutValue}px`
}, 50)

const size = useWindowSize()
watch(size.width, onWindowResize, { immediate: true })
</script>

<style lang="scss" scoped>
.dropdown {
  position: absolute;
  top: calc(100% - #{rem(4px)});
  left: 0;
  z-index: 10;
}

.dropdown-content {
  @include flex-col;
  gap: rem(14px);
  width: max-content;
  min-width: rem(200px);
  max-width: v-bind(maxWidth);

  padding: rem(16px) rem(14px) rem(20px);
  background-color: $layout-background-02;
  border-radius: $border-radius;
  box-shadow: $base-shadow;
  transform: translateX(v-bind(translateX));
}

.list-item {
  max-width: 100%;

  &:not(:last-child) {
    padding-bottom: rem(13px);
    border-bottom: 1px solid $layout-divider;
  }
}
</style>
